import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useApi } from "../../api/ApiProvider";
import { config } from "../../config";
import { useOrg } from "../OrgContext";
import { useUsuariosApi } from "../pages/usuarios/usuariosApi";
import type { FuenteOpciones, OpcionCampo } from "./canvasSchema";

/** Una unidad de medida tal como la devuelve el ERP (campos defensivos por si cambia el nombre del id). */
interface UnidadRemota {
  id?: string;
  unitId?: string;
  code?: string;
  name?: string;
  symbol?: string;
}

/**
 * Opciones que el lienzo carga EN VIVO desde el ERP para un campo 'select-remoto'/'multiselect-remoto':
 * roles de usuario (presets) o sedes de la entidad legal. Así el desplegable refleja lo que hay ahora
 * (los roles son editables) sin listas fijas que se queden viejas.
 */
export function useOpcionesRemotas(fuente: FuenteOpciones): { opciones: OpcionCampo[]; cargando: boolean } {
  const usuariosApi = useUsuariosApi();
  const api = useApi();
  const auth = useAuth();
  const { tenantId, legalEntityId } = useOrg();

  const rolesQ = useQuery({
    queryKey: ["opc-roles"],
    enabled: fuente === "roles-usuario",
    queryFn: () => usuariosApi.presets(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const sedesQ = useQuery({
    queryKey: ["opc-sedes", tenantId, legalEntityId],
    enabled: fuente === "sedes" && legalEntityId !== null,
    queryFn: async () => {
      if (legalEntityId === null) {
        return [];
      }
      const { data } = await api.GET(
        "/api/v1/tenants/{tenantId}/legal-entities/{legalEntityId}/branches",
        { params: { path: { tenantId, legalEntityId } } },
      );
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const almacenesQ = useQuery({
    queryKey: ["opc-almacenes", tenantId, legalEntityId],
    enabled: fuente === "almacenes" && legalEntityId !== null,
    queryFn: async () => {
      if (legalEntityId === null) {
        return [];
      }
      const { data } = await api.GET(
        "/api/v1/tenants/{tenantId}/legal-entities/{legalEntityId}/warehouses",
        { params: { path: { tenantId, legalEntityId } } },
      );
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Unidades: se pide con fetch directo porque el endpoint /units aún no está en el cliente tipado
  // (@farmacenter/api); reenvía el token de la sesión. Degrada a lista vacía si falla o no está.
  const unidadesQ = useQuery({
    queryKey: ["opc-unidades", tenantId],
    enabled: fuente === "unidades",
    queryFn: async (): Promise<UnidadRemota[]> => {
      const token = auth.user?.access_token ?? null;
      const res = await fetch(`${config.apiBaseUrl}/api/v1/tenants/${tenantId}/units`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        return [];
      }
      const datos = (await res.json()) as UnidadRemota[];
      return Array.isArray(datos) ? datos : [];
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (fuente === "roles-usuario") {
    return {
      opciones: (rolesQ.data ?? []).map((p) => ({ value: p.key, label: p.label })),
      cargando: rolesQ.isLoading,
    };
  }
  if (fuente === "unidades") {
    return {
      opciones: (unidadesQ.data ?? []).map((u) => ({
        value: u.id ?? u.unitId ?? "",
        label: [u.code, u.name].filter(Boolean).join(" · ") || u.symbol || (u.id ?? u.unitId ?? ""),
      })),
      cargando: unidadesQ.isLoading,
    };
  }
  if (fuente === "almacenes") {
    return {
      opciones: (almacenesQ.data ?? []).map((w) => ({
        value: w.id ?? "",
        label: [w.code, w.name].filter(Boolean).join(" · ") || (w.id ?? ""),
      })),
      cargando: almacenesQ.isLoading,
    };
  }
  return {
    opciones: (sedesQ.data ?? []).map((b) => ({
      value: b.id ?? "",
      label: [b.code, b.name].filter(Boolean).join(" · ") || (b.id ?? ""),
    })),
    cargando: sedesQ.isLoading,
  };
}
